import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/client';
import { User } from '../types';
import QuotationForm from './QuotationForm';
import QuotationHistory from './QuotationHistory';
import TokenStatus from './TokenStatus';
import AdminPanel from './AdminPanel';
import { 
  History, LogOut, Plus, Menu, X, ArrowLeft, 
  Loader2, Package, Forklift, Container, Warehouse, 
  Boxes, Boxes as BoxesStacked, Shield, Settings,
  HelpCircle, FileText, LayoutDashboard, Info 
} from 'lucide-react';
import { ensureAdminStatus, isUserAdmin } from '../services/tokenService';
import { Link, useNavigate } from 'react-router-dom';
import { analyticsService } from '../services/analyticsService';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import TruckLogo from './TruckLogo';
import BusinessFooter from './BusinessFooter';

interface DashboardProps {
  user: User;
  onUserLoggedOut?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onUserLoggedOut }) => {
  const [activeTab, setActiveTab] = useState<'new' | 'history' | 'admin'>('new');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdvancedMode, setShowAdvancedMode] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const handleSignOut = async () => {
    try {
      // Track sign out event
      await analyticsService.trackCustomEvent('user_sign_out');
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      // Handle AuthSessionMissingError gracefully - this means the session was already invalid
      if (error instanceof Error && (
        error.message.includes('Auth session missing') || 
        error.message.includes('session_not_found') ||
        error.name === 'AuthSessionMissingError'
      )) {
        console.warn('Session was already invalid, sign out completed locally:', error.message);
        // Session is already invalid on server, force logout in App.tsx
        if (onUserLoggedOut) {
          onUserLoggedOut();
        }
        return;
      }
      
      // Log other errors as actual errors
      console.error('Unexpected error during sign out:', error);
    }
  };

  useEffect(() => {
    // Track dashboard page view
    analyticsService.trackPageView('dashboard');

    const checkAdminStatus = async () => {
      setLoading(true);
      try {
        // Check if user is admin
        console.log(`Dashboard: Checking if user ${user.uid} (${user.email}) is admin`);
        const admin = await isUserAdmin(user.uid);
        console.log(`Dashboard: Admin check result for ${user.uid}: ${admin}`);
        setIsAdmin(admin);

        // If the email matches the admin email, ensure admin permissions
        if (user.email === 'autotrindemexico@gmail.com') {
          console.log(`Dashboard: Email ${user.email} matches admin email, ensuring admin status`);
          await ensureAdminStatus(user.uid, user.email);
          setIsAdmin(true);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  // Track tab changes
  useEffect(() => {
    analyticsService.trackCustomEvent('dashboard_tab_change', {
      tab: activeTab
    });
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <header className="bg-primary text-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <TruckLogo size={28} className="text-white mr-2" />
              <h1 className="text-xl font-bold font-heading uppercase">COTIZATRIN</h1>
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 rounded-full hover:bg-primary-dark transition-colors"
                aria-label={menuOpen ? t('common.cancel') : t('common.menu')}
              >
                {menuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
            
            {/* Desktop user info */}
            <div className="hidden md:flex items-center space-x-3">
              <LanguageSwitcher variant="icon" className="mr-3" />
              
              <div className="mr-4">
                <p className="text-sm opacity-80">{t('dashboard.welcome')}</p>
                <p className="font-semibold">{user.displayName || user.email}</p>
              </div>
              
              <button
                onClick={handleSignOut}
                className="bg-primary-dark hover:bg-blue-800 text-white px-4 py-2 rounded-md flex items-center transition duration-200"
              >
                <LogOut size={18} className="mr-1" />
                {t('dashboard.logout')}
              </button>
            </div>
          </div>
          
          {/* Mobile menu */}
          {menuOpen && (
            <div className="mt-4 pt-4 border-t border-white/20 md:hidden">
              <div className="flex flex-col space-y-3">
                <div>
                  <p className="text-sm opacity-80">{t('dashboard.welcome')}</p>
                  <p className="font-semibold">{user.displayName || user.email}</p>
                </div>
                
                <div className="mb-3">
                  <LanguageSwitcher variant="minimal" />
                </div>
                
                <button
                  onClick={handleSignOut}
                  className="bg-primary-dark hover:bg-blue-800 text-white px-4 py-2 rounded-md flex items-center justify-center transition duration-200"
                >
                  <LogOut size={18} className="mr-1" />
                  {t('dashboard.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-6 flex-grow">
        {/* Changed layout to give form more space */}
        <div className="grid grid-cols-1 gap-6">
          <div>
            {/* Navigation Bar - Optimized layout */}
            <div className="mb-5 bg-white rounded-lg shadow-sm p-3">
              <div className="flex flex-col gap-3">
                {/* Title - hidden on mobile when buttons show active state */}
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 font-heading text-center sm:text-left">
                  {activeTab === 'new' ? t('dashboard.newQuotation') :
                   activeTab === 'history' ? t('dashboard.quotationHistory') :
                   t('dashboard.adminPanel')}
                </h2>

                {/* Main Navigation buttons - responsive grid layout */}
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 rounded-lg bg-gray-100 p-1 shadow-inner">
                    <button
                      onClick={() => setActiveTab('new')}
                      className={`flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                        activeTab === 'new'
                          ? 'bg-primary text-white shadow-md'
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Plus size={14} className="mr-1 sm:mr-1.5 flex-shrink-0" />
                      <span className="truncate">{t('dashboard.newQuotation')}</span>
                    </button>

                    <button
                      onClick={() => setActiveTab('history')}
                      className={`flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                        activeTab === 'history'
                          ? 'bg-primary text-white shadow-md'
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <History size={14} className="mr-1 sm:mr-1.5 flex-shrink-0" />
                      <span className="truncate">{t('dashboard.quotationHistory')}</span>
                    </button>

                    {isAdmin && (
                      <button
                        onClick={() => setActiveTab('admin')}
                        className={`flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 col-span-2 sm:col-span-1 ${
                          activeTab === 'admin'
                            ? 'bg-primary text-white shadow-md'
                            : 'text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Shield size={14} className="mr-1 sm:mr-1.5 flex-shrink-0" />
                        <span className="truncate">{t('dashboard.adminPanel')}</span>
                      </button>
                    )}
                  </div>

                  {/* Advanced Mode button - separate row on mobile */}
                  {activeTab === 'new' && (
                    <div className="rounded-lg bg-gray-100 p-1 shadow-inner">
                      <button
                        onClick={() => setShowAdvancedMode(!showAdvancedMode)}
                        className={`flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 w-full ${
                          showAdvancedMode
                            ? 'bg-orange-500 text-white shadow-md'
                            : 'text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Settings size={14} className="mr-1 sm:mr-1.5 flex-shrink-0" />
                        <span>{t('dashboard.advancedMode')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Admin status and token status indicators */}
            {activeTab === 'new' && (
              <div className="mb-5">
                <TokenStatus user={user} showPurchaseOptions={true} />
              </div>
            )}
            
            {/* Secondary actions bar - when in New Quotation mode */}
            {activeTab === 'new' && (
              <div className="mb-5 bg-white rounded-lg shadow-sm p-3">
                <div className="flex flex-col gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 font-heading text-center sm:text-left">
                    {t('dashboard.additionalResources')}
                  </h3>

                  <div className="rounded-lg bg-gray-100 p-1 shadow-inner">
                    <Link
                      to="/guia-formulario"
                      onClick={() => analyticsService.trackCustomEvent('form_guide_click')}
                      className="flex items-center justify-center px-3 py-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 bg-orange-500 text-white shadow-md hover:bg-orange-600 w-full"
                    >
                      <HelpCircle size={14} className="mr-1 sm:mr-1.5 flex-shrink-0" />
                      <span>{t('dashboard.formGuide')}</span>
                    </Link>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'new' ? (
              <QuotationForm 
                userId={user.uid} 
                showAdvancedMode={showAdvancedMode}
                setShowAdvancedMode={setShowAdvancedMode}
                onSaveQuotation={() => {
                  // No cambiar de pestaña - mantener al usuario en la pantalla de cotización
                  // para que pueda ver los resultados y descargar el PDF
                }}
              />
            ) : activeTab === 'history' ? (
              <QuotationHistory userId={user.uid} />
            ) : (
              <AdminPanel user={user} />
            )}
          </div>
        </div>
      </main>
      
      <BusinessFooter />
      
      {/* Floating language switcher for mobile devices */}
      <div className="md:hidden">
        <LanguageSwitcher variant="floating" />
      </div>
    </div>
  );
};

export default Dashboard;